import axios from 'axios'
import { store } from '@/store/index.js'
import { getAuthProvider } from '@/oidc-config.js'

const getAuthorizationHeader = () => {
  const provider = getAuthProvider(store.state.oidc?.user)
  const token = provider === 'SCANIA'
    ? store.state.bearerToken
    : (store.state.oidc?.access_token ?? store.state.bearerToken)

  if (!token) return {}

  return {
    Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`
  }
}

const getBaseUrl = () => {
  const selectedEnv = localStorage.getItem('selectedSASEnvDesc')

  return selectedEnv === 'SNAPROD' || selectedEnv === null
    ? process.env.VUE_APP_SAS_VIYA4_PROD_DOMAIN
    : process.env.VUE_APP_SAS_VIYA4_TEST_DOMAIN
}

const getEnvGuid = () => {
  const selectedEnv = localStorage.getItem('selectedSASEnvDesc')

  return selectedEnv === 'SNAPROD' || selectedEnv === null
    ? process.env.VUE_APP_SAS_VIYA4_PROD_COMPUTE_CONTEXT
    : process.env.VUE_APP_SAS_VIYA4_TEST_COMPUTE_CONTEXT
}

const getGuiApiPath = () =>
  `/SASJobExecution/?_program=/RnDAdvancedAnalyticPlatform${process.env.VUE_APP_API_ENV}/Programs/GUIAPI`

const addRequestConfig = (client) => {
  client.interceptors.request.use((config) => {
    // Read the environment on each request, so changing it takes effect immediately.
    config.baseURL = getBaseUrl()
    config.headers = config.headers ?? {}

    const authHeader = getAuthorizationHeader()
    if (authHeader.Authorization) {
      config.headers.Authorization = authHeader.Authorization
    } else {
      delete config.headers.Authorization
    }

    return config
  })

  return client
}

const baseHttpClient = addRequestConfig(axios.create({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  withCredentials: true
}))

const httpClient = addRequestConfig(axios.create({
  baseURL: getGuiApiPath(),
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
}))

const httpPostClient = addRequestConfig(axios.create({
  baseURL: getGuiApiPath(),
  headers: {
    'Content-Type': 'multipart/form-data'
  },
  withCredentials: true
}))

// POST methods
const setComputeSession = (body) =>
  baseHttpClient.post(`/compute/contexts/${getEnvGuid()}/sessions`, body)

const runSASCode = (sessionId, data) =>
  baseHttpClient.post(`/compute/sessions/${sessionId}/jobs`, data)

const submitFile = (file) => {
  const formData = new FormData()
  formData.append('file', file)

  return baseHttpClient.post(
    '/SASJobExecution/?_program=/RndAdvancedAnalyticPlatformProd/Programs/GUIAPI/UpLoadFile',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  )
}

const getQueryPrediction = (data) =>
  baseHttpClient.post(
    '/microanalyticScore/modules/ensemble_231023_1348b14ae64f44b3b7e8ec046a5c0b7f/steps/score',
    data,
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    }
  )

// GET methods
const pollSASJob = (sessionId, jobId) =>
  baseHttpClient.get(`/compute/sessions/${sessionId}/jobs/${jobId}/state`)

const getJobStatus = (jobId) =>
  baseHttpClient.get(`/jobExecution/jobs/${jobId}/state`)

const getJobResult = (jobId) =>
  baseHttpClient.get(`/SASJobExecution/?_jobexec=/jobExecution/jobs/${jobId}`)

const getResult = (sessionId, refId) =>
  baseHttpClient.get(
    `/compute/sessions/${sessionId}/filerefs/${refId}/content`,
    { headers: { Accept: 'text/plain' } }
  )

const validateComputeSession = (sessionId) =>
  baseHttpClient.get(`/compute/sessions/${sessionId}/state`)

const checkAuthentication = () =>
  baseHttpClient.get('/SASJobExecution/keepalive')

const getDownloadData = (link) =>
  axios.get(link, {
    responseType: 'blob',
    headers: {
      ...getAuthorizationHeader(),
      Accept: 'application/octet-stream'
    },
    onDownloadProgress: (progressEvent) => {
      const dataDownloaded = progressEvent.loaded / 1000 // KB
      store.commit('inspectExportDataSize', dataDownloaded)
    }
  })

const retrieveJobLog = (sessionId) =>
  baseHttpClient.get(`/compute/sessions/${sessionId}/log`)

const putSASDriveCurrentSharedWithMeFolder = () =>
  baseHttpClient.put(
    '/preferences/preferences/PMAUTJ/sas.drive.current.folder',
    {
      application: 'sas.drive',
      id: 'sas.drive.current.folder',
      value: '/driveItems/share:singleton:sasDrive'
    }
  )

const getCsrf = () => baseHttpClient.get('/SASJobExecution/csrf')

const computeSessions = () =>
  baseHttpClient.get('/compute/sessions', {
    headers: {
      Accept: 'application/vnd.sas.collection+json',
      'Accept-Item': 'application/vnd.sas.compute.session+json'
    }
  })

export {
  httpClient,
  httpPostClient,
  getQueryPrediction,
  submitFile,
  getCsrf,
  runSASCode,
  validateComputeSession,
  retrieveJobLog,
  putSASDriveCurrentSharedWithMeFolder,
  pollSASJob,
  computeSessions,
  getResult,
  setComputeSession,
  checkAuthentication,
  getJobStatus,
  getJobResult,
  getDownloadData
}